import { useMemo } from 'react';
import { getMessage } from '@tcc/i18n';
import { RegionReportStats } from '../RegionReportStats/RegionReportStats';
import { parseAppliedApplicationsData } from '../../../../utils/harvestData';
import { YieldRegionReport } from '../../../../types';
import { OnStatsRowClick } from '../container/types/modals';
import { getDataWithFormattedDates } from '../frrStatsFormat';

export interface LegacyRegionReportListProps {
  data: YieldRegionReport;
  areaUnit: string;
  cropCode: string;
  index: number;
  locale: string;
  isAirSeeder: boolean;
  listView?: boolean;
  cropNominalWeight?: number | null;
  onClickItem?: OnStatsRowClick;
}

export const LegacyRegionReportList = ({
  data,
  areaUnit,
  cropCode,
  index,
  locale,
  isAirSeeder,
  listView,
  cropNominalWeight,
  onClickItem,
}: LegacyRegionReportListProps) => {
  const formattedPlanterDateStats = useMemo(() => {
    if(!data.planterDateStats || data.planterDateStats.length === 0) {      
      return data.planterDateStats;
    } 
    return getDataWithFormattedDates(data.planterDateStats, locale);
    
  }, [data, locale]);

  return (
    <>
    {data.applicationStats && data.applicationStats.length > 0 && (
      <RegionReportStats
          areaUnit={areaUnit}
          cropCode={cropCode}
          data={parseAppliedApplicationsData(data.applicationStats)}
          dataTestId="applications" // as applied application
          headerText={getMessage('regions.report.modal.byApplication')}
          index={index}
          locale={locale}
          cropNominalWeight={cropNominalWeight}
        />
      )}
      {data.hybridStats && data.hybridStats.length > 0 && (
        <RegionReportStats
          areaUnit={areaUnit}
          colors={data.hybridColors}
          cropCode={cropCode}
          data={data.hybridStats}
          dataTestId="hybrids"
          headerText={getMessage('regions.report.modal.byHybrid')}
          index={index}
          onClickItem={listView ? onClickItem : undefined}
          showIcon
          cropNominalWeight={cropNominalWeight}
        />
      )}
      {data.soilStats && data.soilStats.length > 0 && (
        <RegionReportStats
          allowHover
          areaUnit={areaUnit}
          cropCode={cropCode}
          data={data.soilStats}
          dataTestId="soil"
          headerText={getMessage('regions.report.modal.bySoil')}
          index={index}
          onClickItem={listView ? onClickItem : undefined}
          showIcon
          cropNominalWeight={cropNominalWeight}
        />
      )}
      {!isAirSeeder &&
        data.populationStats &&
        data.populationStats.length > 0 && (
          <RegionReportStats
            allowHover
            areaUnit={areaUnit}
            cropCode={cropCode}
            data={data.populationStats}
            dataTestId="population"
            headerText={getMessage('regions.report.modal.byPopulation')}
            index={index}
            onClickItem={listView ? onClickItem : undefined}
            showIcon
            displayRanges
            cropNominalWeight={cropNominalWeight}
          />
        )}
      {data.elevationStats && data.elevationStats.length > 0 && (
        <RegionReportStats
          allowHover
          areaUnit={areaUnit}
          cropCode={cropCode}
          data={data.elevationStats}
          dataTestId="elevation"
          headerText={getMessage('regions.report.modal.byElevation')}
          index={index}
          onClickItem={listView ? onClickItem : undefined}
          showIcon
          displayRanges
          cropNominalWeight={cropNominalWeight}
        />
      )}
      {data.planterDateStats && data.planterDateStats.length > 0 && (
        <RegionReportStats
          allowHover
          areaUnit={areaUnit}
          cropCode={cropCode}
          data={formattedPlanterDateStats}
          dataTestId="planterDate"
          headerText={getMessage('regions.report.modal.byPlanterDate')}
          index={index}
          onClickItem={listView ? onClickItem : undefined}
          showIcon
          cropNominalWeight={cropNominalWeight}
        />
      )}
      {data.seedTreatmentStats && data.seedTreatmentStats.length > 0 && (
        <RegionReportStats
          allowHover
          areaUnit={areaUnit}
          cropCode={cropCode}
          data={data.seedTreatmentStats}
          dataTestId="seedTreatment"
          headerText={getMessage('regions.report.modal.bySeedTreatment')}
          index={index}
          onClickItem={listView ? onClickItem : undefined}
          showIcon
          cropNominalWeight={cropNominalWeight}
        />
      )}
    </>
  )
};
